import React from 'react';
import styled from 'styled-components';
import { 
  Hospital, 
  Database, 
  Cloud, 
  TrendingUp, 
  Shield, 
  Settings
} from 'lucide-react';

const ServicesSection = styled.section`
  padding: 5rem 0;
  background-color: #f8f9fa;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 0 1rem;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;

  h2 {
    font-size: 2.5rem;
    color: #333;
    margin-bottom: 1rem;
  }

  p {
    font-size: 1.2rem;
    color: #666;
    max-width: 600px;
    margin: 0 auto;
  }

  @media (max-width: 768px) {
    margin-bottom: 3rem;
    
    h2 {
      font-size: 2rem;
    }
    
    p {
      font-size: 1.1rem;
      padding: 0 1rem;
    }
  }

  @media (max-width: 480px) {
    margin-bottom: 2rem;
    
    h2 {
      font-size: 1.8rem;
    }
    
    p {
      font-size: 1rem;
    }
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
  width: 100%;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }

  @media (max-width: 480px) {
    gap: 1rem;
  }
`;

const ServiceCard = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;
  width: 100%;
  box-sizing: border-box;

  &:hover {
    transform: translateY(-5px);
  }

  .icon {
    font-size: 3rem;
    color: #ff6900;
    margin-bottom: 1rem;
  }

  h3 {
    font-size: 1.5rem;
    color: #333;
    margin-bottom: 1rem;
  }

  p {
    color: #666;
    line-height: 1.6;
    margin-bottom: 1rem;
  }

  ul {
    color: #666;
    padding-left: 1rem;
    
    li {
      margin-bottom: 0.5rem;
    }
  }

  @media (max-width: 768px) {
    padding: 1.5rem;
    
    .icon {
      font-size: 2.5rem;
    }
    
    h3 {
      font-size: 1.3rem;
    }
  }

  @media (max-width: 480px) {
    padding: 1rem;
    
    .icon {
      font-size: 2rem;
    }
    
    h3 {
      font-size: 1.2rem;
    }
  }
`;

const SpecializationBanner = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 3rem;
  border-radius: 15px;
  text-align: center;
  width: 100%;
  box-sizing: border-box;

  h3 {
    font-size: 2rem;
    margin-bottom: 1rem;
  }

  p {
    font-size: 1.1rem;
    opacity: 0.9;
  }

  @media (max-width: 768px) {
    padding: 2rem;
    
    h3 {
      font-size: 1.5rem;
    }
    
    p {
      font-size: 1rem;
    }
  }

  @media (max-width: 480px) {
    padding: 1.5rem;
    
    h3 {
      font-size: 1.3rem;
    }
    
    p {
      font-size: 0.9rem;
    }
  }
`;

const Services: React.FC = () => {
  const services = [
    {
      icon: <Hospital className="icon" />,
      title: "Healthcare Data Engineering",
      description: "Specialized expertise in healthcare data systems, HIPAA compliance, and medical data integration.",
      features: [
        "EHR/EMR data pipeline development",
        "Healthcare analytics platforms",
        "HIPAA-compliant data architectures",
        "Medical device data integration"
      ]
    },
    {
      icon: <Database className="icon" />,
      title: "Data Pipeline Architecture",
      description: "Design and implement robust, scalable data pipelines for modern enterprises.",
      features: [
        "ETL/ELT pipeline development",
        "Real-time data streaming",
        "Data warehouse modernization",
        "API integration solutions"
      ]
    },
    {
      icon: <Cloud className="icon" />,
      title: "Cloud Data Solutions",
      description: "Leverage cloud platforms for scalable, cost-effective data infrastructure.",
      features: [
        "AWS/Azure/GCP data services",
        "Serverless data architectures",
        "Multi-cloud strategies",
        "Cloud migration consulting"
      ]
    },
    {
      icon: <TrendingUp className="icon" />,
      title: "Analytics & BI",
      description: "Transform raw data into actionable insights with modern analytics solutions.",
      features: [
        "Self-service analytics platforms",
        "Custom dashboard development",
        "Advanced analytics workflows",
        "Machine learning pipelines"
      ]
    },
    {
      icon: <Shield className="icon" />,
      title: "Data Governance & Security",
      description: "Ensure data quality, compliance, and security across your organization.",
      features: [
        "Data quality frameworks",
        "Compliance automation",
        "Data lineage tracking",
        "Security implementation"
      ]
    },
    {
      icon: <Settings className="icon" />,
      title: "Data Infrastructure Optimization",
      description: "Optimize existing data systems for performance, scalability, and cost-efficiency.",
      features: [
        "Performance tuning",
        "Cost optimization strategies",
        "Infrastructure automation",
        "Monitoring & alerting"
      ]
    }
  ];

  return (
    <ServicesSection id="services">
      <Container>
        <SectionHeader>
          <h2>Our Data Engineering Services</h2>
          <p>
            Comprehensive data solutions tailored to your industry needs, 
            with specialized expertise in healthcare data systems.
          </p>
        </SectionHeader>

        <ServicesGrid>
          {services.map((service, index) => (
            <ServiceCard key={index}>
              {service.icon}
              <h3>{service.title}</h3>
              <p>{service.description}</p>
              <ul>
                {service.features.map((feature, idx) => (
                  <li key={idx}>{feature}</li>
                ))}
              </ul>
            </ServiceCard>
          ))}
        </ServicesGrid>

        <SpecializationBanner>
          <Hospital style={{ fontSize: '3rem', marginBottom: '1rem' }} />
          <h3>Healthcare Data Expertise</h3>
          <p>
            With extensive experience in healthcare data engineering, we understand the unique challenges 
            of medical data processing, regulatory compliance, and the critical importance of data accuracy 
            in healthcare decision-making.
          </p>
        </SpecializationBanner>
      </Container>
    </ServicesSection>
  );
};

export default Services;